import type { BadgeVariant } from '../../components/ui'
import type {
  LayoutItem,
  LayoutItemCatalogStatus,
  LayoutItemGenerationStatus,
  LayoutItemProductionStatus,
  LayoutJob,
  LayoutJobStatus,
} from './types'

export interface LayoutStatusMeta {
  label: string
  variant: BadgeVariant
}

export const JOB_STATUS: Record<LayoutJobStatus, LayoutStatusMeta> = {
  draft: { label: 'Черновик', variant: 'gray' },
  planning: { label: 'Подготавливается план', variant: 'blue' },
  queued: { label: 'В очереди', variant: 'blue' },
  running: { label: 'Генерируются макеты', variant: 'indigo' },
  paused: { label: 'Приостановлено', variant: 'yellow' },
  completed: { label: 'Макеты созданы', variant: 'green' },
  completed_with_errors: { label: 'Нужна правка', variant: 'yellow' },
  cancelled: { label: 'Остановлено', variant: 'gray' },
}

export const ITEM_GENERATION_STATUS: Record<LayoutItemGenerationStatus, LayoutStatusMeta> = {
  planned: { label: 'Запланирован', variant: 'gray' },
  waiting_assets: { label: 'Генерируются изображения', variant: 'blue' },
  composing: { label: 'Собирается макет', variant: 'indigo' },
  validating: { label: 'Проверяется', variant: 'blue' },
  needs_review: { label: 'Нужна правка', variant: 'yellow' },
  ready: { label: 'Готов', variant: 'green' },
  failed: { label: 'Ошибка', variant: 'red' },
}

export const ITEM_CATALOG_STATUS: Record<LayoutItemCatalogStatus, LayoutStatusMeta> = {
  not_submitted: { label: 'Не отправлен', variant: 'gray' },
  submitting: { label: 'Отправляется', variant: 'blue' },
  // «Только макеты»: лист передан, но производство не запущено — приёмка на этом листе пока не сделала ничего.
  on_acceptance: { label: 'Ждёт приёмки в ПИМ', variant: 'indigo' },
  returned: { label: 'Возвращён', variant: 'yellow' },
  accepted: { label: 'Принят', variant: 'green' },
}

/** Бейдж производства листа; для 'none' и 'done' бейджа нет («Принят» уже сказано статусом каталога). */
export const ITEM_PRODUCTION_STATUS: Partial<Record<LayoutItemProductionStatus, LayoutStatusMeta>> = {
  queued: { label: 'Производство идёт', variant: 'gray' },
  in_progress: { label: 'Производство идёт', variant: 'gray' },
  error: { label: 'Ошибка производства', variant: 'red' },
}

export function itemProductionMeta(item: Pick<LayoutItem, 'productionStatus'>): LayoutStatusMeta | null {
  return ITEM_PRODUCTION_STATUS[item.productionStatus ?? 'none'] ?? null
}

/** Листов с ошибкой производства: серверный счётчик задания, а если он меньше — по самим листам. */
export function jobProductionErrorCount(job: Pick<LayoutJob, 'items' | 'productionErrorCount'>): number {
  const own = job.items.filter((item) => item.productionStatus === 'error').length
  return Math.max(Math.max(0, job.productionErrorCount || 0), own)
}

export function jobDisplayStatus(job: Pick<LayoutJob, 'status' | 'items' | 'productionErrorCount'>): LayoutStatusMeta {
  const total = job.items.length
  const submitted = job.items.filter((item) => item.catalogStatus !== 'not_submitted').length
  const accepted = job.items.filter((item) => item.catalogStatus === 'accepted').length
  const ready = job.items.filter((item) => item.generationStatus === 'ready').length
  const review = job.items.filter((item) => item.generationStatus === 'needs_review').length
  const failed = job.items.filter((item) => item.generationStatus === 'failed').length
  // Ошибка производства важнее «передано / принято»: серия в каталоге, но карточки не пойдут дальше без человека.
  const productionErrors = jobProductionErrorCount(job)
  if (productionErrors > 0) {
    return { label: `Нужна помощь · ошибка производства у ${productionErrors}`, variant: 'red' }
  }
  if (job.items.some((item) => item.catalogStatus === 'returned')) {
    return { label: 'Нужна правка', variant: 'yellow' }
  }
  if (total > 0 && accepted === total) {
    return { label: 'Принято в каталоге', variant: 'green' }
  }
  if (total > 0 && submitted === total) {
    return { label: 'Передано в каталог', variant: 'indigo' }
  }
  if (submitted > 0 && submitted < job.items.length) {
    return { label: 'Отправлено частично', variant: 'indigo' }
  }
  if (review > 0) {
    return {
      label:
        ready > 0
          ? `${ready} из ${total} готовы к отправке`
          : 'Макеты созданы — требуется проверка',
      variant: 'yellow',
    }
  }
  if (failed > 0 && ['completed', 'completed_with_errors'].includes(job.status)) {
    return { label: `Создано ${total - failed} из ${total}`, variant: 'yellow' }
  }
  if (job.status === 'completed' && total > 0 && ready === total) {
    return {
      label: ready === 1 ? '1 макет готов к отправке' : `${ready} макетов готовы к отправке`,
      variant: 'green',
    }
  }
  return JOB_STATUS[job.status]
}

export const LAYOUT_TYPE_LABELS = {
  circle: 'Круг',
  cupcake_5: 'Капкейки · 5',
  cupcake_6: 'Капкейки · 6',
  bento: 'Бенто',
  topper: 'Топперы',
} as const

export function formatMoney(value: number, currency = 'RUB'): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0)
}

/**
 * Статусы карточки площадки (`marketplace_cards.status`) по-русски для паспорта серии (03.10.2026).
 * Известных статусов больше, чем у черновиков: на WB встречаются error и stock_set, у OZON/ЯМ — created.
 * Неизвестный код `cardStatusMeta` возвращает как есть, чтобы не терять данные.
 */
export const CARD_STATUS_LABELS: Record<string, LayoutStatusMeta> = {
  draft: { label: 'Черновик', variant: 'gray' },
  on_approval: { label: 'Черновик готов', variant: 'yellow' },
  creating: { label: 'Отправляется', variant: 'yellow' },
  created: { label: 'Создана на площадке', variant: 'blue' },
  price_set: { label: 'Цена задана', variant: 'blue' },
  stock_set: { label: 'Остаток задан', variant: 'blue' },
  published: { label: 'Опубликована', variant: 'green' },
  selling: { label: 'Историческая отметка PIM', variant: 'gray' },
  error: { label: 'Ошибка', variant: 'red' },
}

export function cardStatusMeta(code: string): LayoutStatusMeta {
  return CARD_STATUS_LABELS[code] ?? { label: code, variant: 'gray' }
}

/**
 * Причина появления версии листа (`ai_layout_revisions.reason`) по-русски: в истории раньше светилось «generated».
 * Значения — из `LayoutRevision['reason']` сервера (design-service/layout-generator/types.ts).
 */
export const REVISION_REASON_LABELS: Record<string, string> = {
  generated: 'Создана при сборке листа',
  manual_edit: 'Правка в редакторе',
  text_edit: 'Правка надписей',
  regenerate: 'Исправление макета ИИ',
  return_fix: 'Исправление после возврата с приёмки',
  restore: 'Возврат к прежней версии',
}

/** Причина версии словами; пустая — «Создание версии»; неизвестный служебный код — тоже «Создание версии». */
export function revisionReasonText(reason: string | null | undefined): string {
  const text = typeof reason === 'string' ? reason.trim() : ''
  if (!text) return 'Создание версии'
  if (REVISION_REASON_LABELS[text]) return REVISION_REASON_LABELS[text]
  return /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$|^[a-z]{4,}$/.test(text) ? 'Создание версии' : text
}
